import { z } from "zod";
// Language proficiency levels following CEFR framework
export var Level;
(function (Level) {
    Level["A1_1"] = "A1.1";
    Level["A1_2"] = "A1.2";
    Level["A2_1"] = "A2.1";
    Level["A2_2"] = "A2.2";
    Level["B1_1"] = "B1.1";
    Level["B1_2"] = "B1.2";
    Level["B2_1"] = "B2.1";
    Level["B2_2"] = "B2.2";
    Level["C1_1"] = "C1.1";
    Level["C1_2"] = "C1.2";
    Level["C2_1"] = "C2.1";
    Level["C2_2"] = "C2.2";
    Level["A1"] = "A1";
    Level["A2"] = "A2";
    Level["B1"] = "B1";
    Level["B2"] = "B2";
    Level["C1"] = "C1";
    Level["C2"] = "C2";
})(Level || (Level = {}));
// Schema for level validation
export const LevelSchema = z.string().refine((val) => Object.values(Level).includes(val), { message: "Invalid level" });
// Schema for course translations (embedded in DynamoDB item)
export const CourseTranslationSchema = z.object({
    name: z.string().min(1),
    description: z.string().nullable().optional(),
});
// Pre-computed counts for DynamoDB denormalization
export const CourseStatsSchema = z.object({
    module_count: z.number().min(0).default(0),
    exercise_count: z.number().min(0).default(0),
    enrolled_count: z.number().min(0).default(0),
    published_module_count: z.number().min(0).default(0),
    published_exercise_count: z.number().min(0).default(0),
});
// Course package (pricing tier)
export const CoursePackageNameEnum = z.enum(["self_study", "standard", "maximum"]);
export const CoursePackageSchema = z.object({
    name: CoursePackageNameEnum,
    stripe_price_id: z.string().nullable().optional(),
    price: z.string(), // EUR price as string e.g. "75.00"
    features: z.array(z.string()),
});
// Main Course schema for DynamoDB
export const CourseSchema = z.object({
    id: z.number().int().optional(),
    name: z.string().max(255),
    description: z.string().nullable(),
    slug: z.string().nullable().optional(),
    target_language_code: z.string().nullable().optional(),
    is_published: z.boolean().default(false),
    is_free: z.boolean().optional(),
    is_hidden: z.boolean().optional(),
    price: z.string().nullable().optional(),
    packages: z.array(CoursePackageSchema).optional(),
    estimated_duration_hours: z.number().nullable().optional(),
    order_index: z.number().nullable().optional(),
    thumbnail_url: z.string().nullable().optional(),
    level: LevelSchema,
    created_at: z.date().optional(),
    updated_at: z.date().optional(),
    // Embedded translations (DynamoDB denormalization)
    translations: z.record(z.string(), CourseTranslationSchema).optional(),
    // Pre-computed stats (DynamoDB denormalization)
    stats: CourseStatsSchema.optional(),
});
// Schema for creating/updating courses
export const CoursePayloadSchema = z.object({
    id: z.number().optional(),
    name: z.string().min(1),
    description: z.string().nullable().optional(),
    slug: z.string().nullable().optional(),
    target_language_code: z.string().nullable().optional(),
    is_published: z.boolean().optional(),
    is_free: z.boolean().optional(),
    is_hidden: z.boolean().optional(),
    price: z.string().nullable().optional(),
    packages: z.array(CoursePackageSchema).optional(),
    estimated_duration_hours: z.number().nullable().optional(),
    order_index: z.number().nullable().optional(),
    thumbnail_url: z.string().nullable().optional(),
    level: LevelSchema,
    translations: z.record(z.string(), CourseTranslationSchema).optional(),
});
// Schema for course with modules (used in queries that need related data)
export const CourseWithModulesSchema = CourseSchema.extend({
    modules: z.array(z.unknown()).optional(), // Module[] - uses z.unknown() to avoid circular deps with module.ts
});
